import React, { useEffect, useState, useCallback } from "react";
import SuperAdminLayout from "@/components/SuperAdminLayout";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  PlugZap,
  Mail,
  CreditCard,
  MessageSquare,
  KeyRound,
  CheckCircle2,
  XCircle,
  Trash2,
  ToggleLeft,
  ToggleRight,
  Database,
  Server,
  CircleSlash,
  ShieldAlert,
  Clock3,
  RotateCw,
  Send,
  UserPlus,
  UserRoundCheck,
  AlertTriangle,
  Clock,
  Copy,
  ExternalLink,
  Zap,
  Webhook,
  Loader2,
  ChevronDown,
  ChevronUp,
  Save,
} from "lucide-react";

const INTEGRATIONS = [
  {
    key: "resend",
    label: "Resend",
    icon: Mail,
    accent: "from-orange-50 to-orange-100/30 border-orange-200",
    description: "Transactional email — invitations, host-arrival, weekly digest, SAR / erasure links.",
    keyHint: "Starts with re_, ≥10 chars",
  },
  {
    key: "stripe",
    label: "Stripe",
    icon: CreditCard,
    accent: "from-indigo-50 to-indigo-100/30 border-indigo-200",
    description: "Subscriptions, additional-site purchases, customer portal, webhooks.",
    keyHint: "sk_live_… or sk_test_…, ≥20 chars",
  },
  {
    key: "twilio",
    label: "Twilio",
    icon: MessageSquare,
    accent: "from-emerald-50 to-emerald-100/30 border-emerald-200",
    description: "SMS — host-arrival fallback when push / email unavailable.",
    keyHint: "Use API Key (Standard) — recommended. Auth Token is a legacy fallback.",
  },
  {
    // v2 PR-2C.1 — Google OAuth credentials for the calendar auto-sync flow.
    key: "google_oauth",
    label: "Google OAuth",
    icon: KeyRound,
    accent: "from-sky-50 to-sky-100/30 border-sky-200",
    description: "Calendar auto-sync — client_id (public) + client_secret (encrypted) for the OAuth dance.",
    keyHint: "Client ID ends in .apps.googleusercontent.com; client_secret 24-40 chars",
  },
];

const PathBadge = ({ path }) => {
  const cfg = path === "db"
    ? { label: "Active: DB", icon: Database, colour: "bg-success-50 text-success-700 border-success-200" }
    : path === "env"
      ? { label: "Active: Env", icon: Server, colour: "bg-warning-50 text-warning-700 border-warning-200" }
      : { label: "Active: Mock", icon: CircleSlash, colour: "bg-navy-50 text-navy-600 border-navy-200" };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${cfg.colour}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

export default function SuperAdminIntegrations() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/super-admin/integrations");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  if (!authLoading && user?.role !== "super_admin") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <SuperAdminLayout>
      <div className="space-y-6" data-testid="super-integrations-page">
        <div>
          <div className="eb-overline mb-1">Module 15.7-ext</div>
          <h1 className="text-3xl font-bold text-navy-900 dark:text-white inline-flex items-center gap-2">
            <PlugZap className="w-7 h-7 text-orange-500" /> Live integration credentials
          </h1>
          <p className="text-sm text-navy-500 dark:text-navy-300 mt-2 max-w-3xl">
            Store live API keys for Resend, Stripe, and Twilio. Keys are encrypted at rest with
            AES-256-GCM under <code className="px-1 rounded bg-navy-50 dark:bg-navy-700 dark:text-navy-100">INTEGRATION_SECRETS_KEY</code> and
            <strong> never returned</strong> by any endpoint — only a fingerprint
            (<code className="px-1 rounded bg-navy-50 dark:bg-navy-700 dark:text-navy-100">{`{last_4_chars}:{sha256_full[:8]}`}</code>).
            The <strong>Active path</strong> indicator on each card shows where the resolved value comes
            from — DB-stored, env var, or mock.
          </p>
          <div className="mt-3 inline-flex items-center gap-2 text-xs text-warning-700 bg-warning-50 border border-warning-200 px-3 py-1.5 rounded-md">
            <ShieldAlert className="w-3.5 h-3.5" />
            Pasting a live key here is irreversible — there is no "view key" action. Test the connection
            after saving to verify the key is correct.
          </div>

          <BuildFingerprint />
        </div>

        {loading && <div className="text-navy-400">Loading…</div>}

        {!loading && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {INTEGRATIONS.map((cfg) => {
              const meta = items.find((i) => i.integration_key === cfg.key) || {
                integration_key: cfg.key, fingerprint: null, mode: null,
                last_rotated_at: null, last_rotated_by_user_id: null, active_path: "mock",
              };
              return <IntegrationCard key={cfg.key} cfg={cfg} meta={meta} onChanged={refresh} />;
            })}
          </div>
        )}

        <ProvisionSuperAdminCard />
      </div>
    </SuperAdminLayout>
  );
}

// ----- v1.1.1 — provision/elevate a super_admin via the live email path -----
function ProvisionSuperAdminCard() {
  const [email, setEmail] = useState("");
  const [elevate, setElevate] = useState(false);
  const [working, setWorking] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!email.trim()) { toast.error("Email is required"); return; }
    setResult(null);
    setWorking(true);
    try {
      const { data } = await api.post("/super-admin/provision-super-admin",
                                       { email: email.trim(), elevate });
      setResult(data);
      toast.success(
        data.reset_link_sent
          ? `Sent — ${email} should receive a magic link via Resend (provider id ${data.provider_id || "—"}).`
          : `Provisioned in ${data.adapter_mode} mode — magic link not delivered (see note below).`,
      );
    } catch (e) {
      const status = e?.response?.status;
      const det = e?.response?.data?.detail;
      if (status === 409 && det?.detail === "user_exists") {
        const existing_role = det.existing_role || "?";
        const existing_org_id = det.existing_org_id ?? "null";
        setResult({ error: "user_exists", existing_role, existing_org_id });
        toast.error(`User exists with role "${existing_role}" (org_id ${existing_org_id}). Tick "Elevate existing user" to override.`);
      } else if (status === 422) {
        toast.error("Bad email format");
      } else if (status === 429) {
        toast.error(`Rate limited — retry in ${det?.retry_after_seconds || "?"}s`);
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setWorking(false); }
  };

  return (
    <div className="eb-light-island bg-white border border-navy-100 rounded-2xl p-5" data-testid="provision-super-admin-card">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="eb-overline mb-1">Operations</div>
          <h2 className="text-lg font-semibold text-navy-900 inline-flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-orange-500" /> Provision super admin
          </h2>
          <p className="text-sm text-navy-500 mt-1 max-w-2xl">
            Create a new <strong>super_admin</strong> account (or elevate an existing user) and email them
            a 1-hour magic password-reset link via the live Resend integration. Replaces the legacy
            direct-DB-write provisioning trick. Audited end-to-end.
          </p>
        </div>
      </div>
      <div className="mt-4 grid sm:grid-cols-[1fr_auto_auto] gap-3 items-end">
        <div>
          <label className="eb-label">Email</label>
          <input
            className="eb-input"
            type="email"
            autoComplete="off"
            placeholder="newadmin@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="provision-super-admin-email"
          />
        </div>
        <label className="text-xs text-navy-700 inline-flex items-center gap-2 pb-2.5">
          <input
            type="checkbox"
            checked={elevate}
            onChange={(e) => setElevate(e.target.checked)}
            data-testid="provision-super-admin-elevate"
          />
          Elevate existing user if they already have a non-super_admin role
        </label>
        <Button
          onClick={submit}
          disabled={working}
          className="bg-orange-500 hover:bg-orange-600 text-white"
          data-testid="provision-super-admin-btn"
        >
          {working ? "Provisioning…" : (<><UserPlus className="w-4 h-4 mr-1" /> Provision</>)}
        </Button>
      </div>
      {result && (
        <pre
          className="mt-4 bg-navy-50 border border-navy-100 rounded-md p-3 text-xs text-navy-800 overflow-x-auto"
          data-testid="provision-super-admin-result"
        >
{JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
}

function IntegrationCard({ cfg, meta, onChanged }) {
  const Icon = cfg.icon;
  const [working, setWorking] = useState(false);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [keyInput, setKeyInput] = useState("");
  const [sidInput, setSidInput] = useState("");
  const [tokenInput, setTokenInput] = useState("");
  // Phase C1.2 — Twilio now collects the full API-Key (Standard) pair
  // plus a sender number. Auth-Token is kept as a fallback only.
  const [apiKeySidInput, setApiKeySidInput] = useState("");
  const [apiKeySecretInput, setApiKeySecretInput] = useState("");
  const [fromNumberInput, setFromNumberInput] = useState("");
  // v2 PR-2C.1 — Google OAuth needs a separate (client_id, client_secret) pair.
  const [clientIdInput, setClientIdInput] = useState("");
  const [clientSecretInput, setClientSecretInput] = useState("");
  const [keyMode, setKeyMode] = useState("live");
  const [testResult, setTestResult] = useState(null);
  // v2 PR-2A — Stripe-specific: dedicated probe response + write-test result.
  const [stripeProbe, setStripeProbe] = useState(null);     // {status, message, latency_ms, active_path, available_count, pending_count, stripe_error_summary}
  const [stripeCustomer, setStripeCustomer] = useState(null); // {ok, partial?, customer_id?, error_summary?, duration_ms, ...}

  const isSet = !!meta.fingerprint;
  const isLive = meta.mode === "live";

  const save = async () => {
    // Phase C1.2 — client-side guard mirrors the server-side rule so
    // typos don't cost a server round-trip: account_sid + from_number
    // are mandatory; auth must be either api_key pair or auth_token.
    if (cfg.key === "twilio") {
      if (!sidInput.trim()) { toast.error("Account SID is required."); return; }
      if (!fromNumberInput.trim()) { toast.error("From number is required (E.164, e.g. +447700900123)."); return; }
      const hasApiKey = apiKeySidInput.trim() && apiKeySecretInput.trim();
      const hasAuthToken = !!tokenInput.trim();
      if (!hasApiKey && !hasAuthToken) {
        toast.error("Provide either an API Key SID + Secret (recommended) OR an Auth Token.");
        return;
      }
    }
    setWorking(true);
    try {
      let payload;
      if (cfg.key === "twilio") {
        payload = {
          account_sid: sidInput.trim(),
          api_key_sid: apiKeySidInput.trim(),
          api_key_secret: apiKeySecretInput.trim(),
          auth_token: tokenInput.trim(),
          from_number: fromNumberInput.trim(),
          mode: keyMode,
        };
      } else if (cfg.key === "google_oauth") {
        payload = {
          client_id: clientIdInput.trim(),
          client_secret: clientSecretInput.trim(),
          mode: keyMode,
        };
      } else {
        payload = { api_key: keyInput.trim(), mode: keyMode };
      }
      const { data } = await api.post(`/super-admin/integrations/${cfg.key}`, payload);
      // Phase C1.2.1 — if the backend trimmed whitespace from any field,
      // surface that as a second-line warning toast so the operator can
      // catch paste errors before the live test fails.
      if (data.note) {
        toast.warning(data.note, { duration: 7000 });
      }
      toast.success(`${cfg.label} saved · fingerprint ${data.fingerprint}`);
      setShowSaveForm(false);
      setKeyInput(""); setSidInput(""); setTokenInput("");
      setApiKeySidInput(""); setApiKeySecretInput(""); setFromNumberInput("");
      setClientIdInput(""); setClientSecretInput("");
      setTestResult(null);
      await onChanged();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.error === "invalid_format") {
        toast.error(`Format error — ${det.message}`);
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setWorking(false); }
  };

  const test = async () => {
    setWorking(true);
    setTestResult(null);
    setStripeProbe(null);
    setStripeCustomer(null);
    try {
      const { data } = await api.post(`/super-admin/integrations/${cfg.key}/test`);
      // v2 PR-2A / PR-2C.1 — Stripe AND Google OAuth use the rich
      // {status: connected|invalid|expired|restricted|error, ...} shape.
      // Resend / Twilio keep the legacy {success, error_class, ...} shape.
      if (cfg.key === "stripe" || cfg.key === "google_oauth") {
        setStripeProbe({ ...data, _integration: cfg.key });
        const verb = cfg.key === "stripe" ? "Stripe" : "Google OAuth";
        if (data.status === "connected") {
          toast.success(`${verb} credentials verified in ${data.latency_ms} ms`);
        } else if (data.status === "restricted") {
          toast.message(`${verb} key valid but restricted.`);
        } else if (data.status === "invalid") {
          toast.error(`${verb} rejected the credentials.`);
        } else if (data.status === "expired") {
          toast.error(`${verb} credential expired.`);
        } else {
          toast.error(`${verb} test failed: ${data.stripe_error_summary || data.google_error_summary || data.message || "unknown"}`);
        }
      } else {
        setTestResult(data);
        if (data.success) {
          toast.success(`${cfg.label} reachable in ${data.latency_ms} ms`);
        } else if (data.restricted || data.error_class === "restricted_api_key") {
          toast.message(`${cfg.label} key is valid (sending-only scope) — use 'Send test email' to verify deliverability.`);
        } else {
          toast.error(`${cfg.label} test failed: ${data.error_class || "unknown"}`);
        }
      }
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 429) {
        toast.error(`Rate limited — retry in ${det?.retry_after_seconds || "?"}s (${det?.limit || "10/hr"})`);
      } else if (det?.error === "no_credentials") {
        toast.error("No credentials set — save a key first");
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setWorking(false); }
  };

  // v2 PR-2A — Stripe write-test (atomic create→delete on /v1/customers).
  // Verifies write scope for sk_live keys without leaving an orphan customer
  // in the live Stripe account. If the delete fails after the create, the
  // backend returns `partial: "created_not_deleted"` + the orphan customer_id
  // so the operator can manually delete it in their Stripe dashboard.
  const testCustomer = async () => {
    setWorking(true);
    setStripeCustomer(null);
    try {
      const { data } = await api.post(`/super-admin/integrations/stripe/test-customer`);
      setStripeCustomer(data);
      if (data.ok) {
        toast.success(`Customer ${data.created?.customer_id} created & deleted in ${data.duration_ms} ms`);
      } else if (data.partial === "created_not_deleted") {
        toast.error(
          `Orphan customer ${data.customer_id} created but NOT deleted — delete it manually in Stripe.`,
          { duration: 10000 },
        );
      } else {
        toast.error(`Stripe customer test failed — ${data.error_summary || "see audit log"}`);
      }
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 429) {
        toast.error(`Rate limited — retry in ${det?.retry_after_seconds || "?"}s (${det?.limit || "5/hr"})`);
      } else if (det?.error === "no_credentials") {
        toast.error("No Stripe credentials set — save a key first");
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setWorking(false); }
  };

  const toggleMode = async () => {
    const newMode = isLive ? "mock" : "live";
    if (!window.confirm(`Switch ${cfg.label} to ${newMode.toUpperCase()} mode? The stored key is preserved either way.`)) return;
    setWorking(true);
    try {
      await api.patch(`/super-admin/integrations/${cfg.key}/mode`, { mode: newMode });
      toast.success(`${cfg.label} mode → ${newMode}`);
      await onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setWorking(false); }
  };

  const clear = async () => {
    if (!window.confirm(
      `Clear the stored ${cfg.label} secret?\n\n` +
      `The adapter will fall back to env var (or mock if unset). The cleared key's fingerprint will be written to the audit log so the rotation is provable.`,
    )) return;
    setWorking(true);
    try {
      const { data } = await api.delete(`/super-admin/integrations/${cfg.key}`);
      toast.success(`Cleared. Active path now: ${data.active_path.toUpperCase()}`);
      await onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setWorking(false); }
  };

  // v1.1.1 — Resend-only: fire a real one-line "EntryBook live test" email to the
  // logged-in super_admin to prove live wiring end-to-end.
  const testSend = async () => {
    setWorking(true);
    try {
      const { data } = await api.post(`/super-admin/integrations/resend/test-send`);
      if (data.success) {
        toast.success(`Sent — check ${data.recipient} (provider id ${data.provider_id || "—"}, ${data.latency_ms} ms)`);
      } else {
        toast.error(`Test send failed: mode=${data.mode}, ${data.error || "see audit log"}`);
      }
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (e?.response?.status === 429) {
        toast.error(`Rate limited — retry in ${det?.retry_after_seconds || "?"}s`);
      } else {
        toast.error(formatApiError(e));
      }
    } finally { setWorking(false); }
  };

  return (
    <div className={`eb-light-island rounded-2xl bg-gradient-to-br ${cfg.accent} border-2 p-5`}
         data-testid={`integration-card-${cfg.key}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-white shadow-sm flex items-center justify-center">
            <Icon className="w-5 h-5 text-navy-700" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-navy-900">{cfg.label}</h3>
            <p className="text-xs text-navy-600 mt-0.5 max-w-md">{cfg.description}</p>
          </div>
        </div>
        <PathBadge path={meta.active_path} />
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
        <div className="bg-white/60 rounded-lg p-3 border border-white">
          <div className="eb-overline mb-1">Fingerprint</div>
          <div className="font-mono text-navy-800" data-testid={`fingerprint-${cfg.key}`}>
            {meta.fingerprint || <span className="text-navy-400">— not set —</span>}
          </div>
        </div>
        <div className="bg-white/60 rounded-lg p-3 border border-white">
          <div className="eb-overline mb-1">Mode</div>
          <div className="font-semibold">
            {meta.mode === "live"
              ? <span className="text-success-700">LIVE</span>
              : meta.mode === "mock"
                ? <span className="text-warning-700">MOCK</span>
                : <span className="text-navy-400">— not set —</span>}
          </div>
        </div>
        {meta.last_rotated_at && (
          <div className="col-span-2 bg-white/60 rounded-lg p-3 border border-white text-navy-600 inline-flex items-center gap-2">
            <Clock3 className="w-3 h-3" /> last rotated {meta.last_rotated_at.replace("T", " ").slice(0, 19)}Z
          </div>
        )}
        {/* Phase C1.2.1 — Twilio "saved credentials" panel — surfaces auth
            mode, From-Number, and the last-4 of every credential field
            behind a "Show last 4" toggle so the operator can sanity-
            check what's actually in the vault. */}
        {cfg.key === "twilio" && meta.hints && (
          <TwilioHintsPanel hints={meta.hints} />
        )}
        {cfg.key === "web_push" && meta.hints && (
          <div className="col-span-2 bg-white/60 rounded-lg p-3 border border-white text-navy-700 space-y-1" data-testid="web-push-hints">
            <div className="flex items-center justify-between gap-2">
              <span className="text-navy-500">VAPID subject</span>
              <span className="font-mono text-[11px]">{meta.hints.subject}</span>
            </div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-navy-500">Public key</span>
              <span className="font-mono text-[11px]">…{meta.hints.public_key_last8}</span>
            </div>
          </div>
        )}
        {testResult && cfg.key !== "stripe" && cfg.key !== "google_oauth" && (
          cfg.key === "twilio" && !testResult.success && !testResult.restricted
            ? <TwilioFailureRenderer result={testResult} />
            : <div className={`col-span-2 rounded-lg p-3 border text-xs ${
                testResult.success
                  ? "bg-success-50 border-success-200 text-success-700"
                  : testResult.restricted || testResult.error_class === "restricted_api_key"
                    ? "bg-warning-50 border-warning-300 text-warning-700"
                    : "bg-danger-50 border-danger-200 text-danger-700"
              }`} data-testid={`test-result-${cfg.key}`}>
                {testResult.success
                  ? <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Reachable in {testResult.latency_ms} ms · path {testResult.active_path}</span>
                  : testResult.restricted || testResult.error_class === "restricted_api_key"
                    ? <span className="inline-flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> Scoped key — sending-only (recommended). Use <strong>Send test email</strong> to verify.</span>
                    : <span className="inline-flex items-center gap-1"><XCircle className="w-3 h-3" /> Failed ({testResult.error_class}) — {testResult.error}</span>}
              </div>
        )}

        {/* v2 PR-2A / PR-2C.1 — shared status-pill panel for integrations whose
            probe returns the {status:connected|invalid|...} shape. */}
        {(cfg.key === "stripe" || cfg.key === "google_oauth") && stripeProbe && (
          <StripeProbeResult probe={stripeProbe} integration={cfg.key} />
        )}

        {/* v2 PR-2A — Stripe customer write-test result + orphan banner. */}
        {cfg.key === "stripe" && stripeCustomer && (
          <StripeCustomerResult result={stripeCustomer} />
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button size="sm"
                onClick={() => setShowSaveForm((v) => !v)}
                disabled={working}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid={`save-btn-${cfg.key}`}>
          <KeyRound className="w-4 h-4 mr-1" /> {isSet ? "Rotate / re-save" : "Save key"}
        </Button>
        <Button size="sm" variant="outline" onClick={test} disabled={working || !isSet}
                data-testid={`test-btn-${cfg.key}`}>
          <RotateCw className={`w-4 h-4 mr-1 ${working ? "animate-spin" : ""}`} /> Test connection
        </Button>
        {cfg.key === "resend" && (
          <Button size="sm" variant="outline" onClick={testSend} disabled={working || !isSet || !isLive}
                  data-testid={`test-send-btn-${cfg.key}`}>
            <Send className="w-4 h-4 mr-1" /> Send test email
          </Button>
        )}
        {cfg.key === "stripe" && (
          <Button size="sm" variant="outline" onClick={testCustomer} disabled={working || !isSet || !isLive}
                  data-testid={`test-customer-btn-${cfg.key}`}>
            <UserRoundCheck className="w-4 h-4 mr-1" /> Send test customer
          </Button>
        )}
        <Button size="sm" variant="outline" onClick={toggleMode} disabled={working || !isSet}
                data-testid={`toggle-btn-${cfg.key}`}>
          {isLive
            ? <><ToggleRight className="w-4 h-4 mr-1 text-success-600" /> Mode: live</>
            : <><ToggleLeft className="w-4 h-4 mr-1 text-warning-600" /> Mode: mock</>}
        </Button>
        {isSet && (
          <Button size="sm" variant="ghost" onClick={clear} disabled={working}
                  className="text-danger-600 hover:bg-danger-50"
                  data-testid={`clear-btn-${cfg.key}`}>
            <Trash2 className="w-4 h-4 mr-1" /> Clear
          </Button>
        )}
      </div>

      {showSaveForm && (
        <div className="mt-4 bg-white rounded-xl p-4 border border-navy-100 space-y-3"
             data-testid={`save-form-${cfg.key}`}>
          <div>
            <p className="text-xs text-navy-500 mb-2">{cfg.keyHint}</p>
            {cfg.key === "twilio" ? (
              <>
                <div className="mb-3 rounded-md border border-emerald-200 bg-emerald-50/50 px-3 py-2 text-[11px] text-emerald-900 leading-relaxed">
                  Use <strong>API Key (Standard)</strong> for production. The Auth Token field is a fallback —
                  leave blank if you've set API Key SID and Secret.
                </div>
                <label className="eb-label">Account SID <span className="text-danger-600">*</span></label>
                <input className="eb-input mb-3" type="text" autoComplete="off"
                       value={sidInput} onChange={(e) => setSidInput(e.target.value)}
                       placeholder="AC… (34 chars, starts with AC)"
                       data-testid="vault-input-twilio-account-sid" />

                <label className="eb-label">API Key SID <span className="text-emerald-700 text-[10px] uppercase tracking-wider ml-1">recommended</span></label>
                <input className="eb-input mb-3" type="text" autoComplete="off"
                       value={apiKeySidInput} onChange={(e) => setApiKeySidInput(e.target.value)}
                       placeholder="SK… (34 chars, starts with SK)"
                       data-testid="vault-input-twilio-api-key-sid" />

                <label className="eb-label">API Key Secret <span className="text-emerald-700 text-[10px] uppercase tracking-wider ml-1">recommended</span></label>
                <input className="eb-input mb-3" type="password" autoComplete="off"
                       value={apiKeySecretInput} onChange={(e) => setApiKeySecretInput(e.target.value)}
                       placeholder="Twilio API key secret"
                       data-testid="vault-input-twilio-api-key-secret" />

                <label className="eb-label text-navy-500">Legacy Auth Token (fallback)</label>
                <input className="eb-input mb-3" type="password" autoComplete="off"
                       value={tokenInput} onChange={(e) => setTokenInput(e.target.value)}
                       placeholder="32 hex chars — only if not using API Key"
                       data-testid="vault-input-twilio-auth-token" />

                <label className="eb-label">From Number <span className="text-danger-600">*</span></label>
                <input className="eb-input" type="text" autoComplete="off"
                       value={fromNumberInput} onChange={(e) => setFromNumberInput(e.target.value)}
                       placeholder="+447700900123 (E.164)"
                       data-testid="vault-input-twilio-from-number" />
              </>
            ) : cfg.key === "google_oauth" ? (
              <>
                <label className="eb-label">Client ID</label>
                <input className="eb-input mb-2" type="text" autoComplete="off"
                       value={clientIdInput} onChange={(e) => setClientIdInput(e.target.value)}
                       placeholder="123456789-abc.apps.googleusercontent.com"
                       data-testid="vault-input-google-oauth-client-id" />
                <label className="eb-label">Client Secret</label>
                <input className="eb-input" type="password" autoComplete="off"
                       value={clientSecretInput} onChange={(e) => setClientSecretInput(e.target.value)}
                       placeholder="GOCSPX-…"
                       data-testid="vault-input-google-oauth-client-secret" />
              </>
            ) : (
              <>
                <label className="eb-label">API key</label>
                <input className="eb-input" type="password" autoComplete="off"
                       value={keyInput} onChange={(e) => setKeyInput(e.target.value)}
                       placeholder={cfg.key === "resend" ? "re_..." : "sk_live_..."}
                       data-testid={`save-key-${cfg.key}`} />
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <label className="text-xs text-navy-700 inline-flex items-center gap-2">
              <input type="radio" name={`mode-${cfg.key}`} checked={keyMode === "live"} onChange={() => setKeyMode("live")} />
              Live
            </label>
            <label className="text-xs text-navy-700 inline-flex items-center gap-2">
              <input type="radio" name={`mode-${cfg.key}`} checked={keyMode === "mock"} onChange={() => setKeyMode("mock")} />
              Mock (key stored, but adapter still uses mock)
            </label>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={working}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid={`save-confirm-${cfg.key}`}>
              {working ? "Saving…" : "Save & encrypt"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => {
              setShowSaveForm(false);
              setKeyInput(""); setSidInput(""); setTokenInput("");
              setApiKeySidInput(""); setApiKeySecretInput(""); setFromNumberInput("");
              setClientIdInput(""); setClientSecretInput("");
            }}>
              Cancel
            </Button>
          </div>
          <p className="text-xs text-navy-400">
            Plaintext is encrypted in-flight, never logged, and never returned by any GET endpoint.
          </p>
        </div>
      )}

      {/* Phase C2.1 — Stripe-only one-click setup panel. Hidden for every
          other integration card so we don't repeat the same UI four times. */}
      {cfg.key === "stripe" && <StripeLiveSetupPanel meta={meta} />}
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Phase C2.1 — Stripe one-click live setup panel
// Mounted only on the Stripe card. Drives:
//   • POST /api/super-admin/stripe/run-setup    (4 SKUs in one click)
//   • POST /api/super-admin/stripe/webhook-test (verify a whsec_ paste)
// Gated on `meta.mode === "live"` + a stored fingerprint — otherwise we
// render a yellow "Awaiting credentials" chip so the operator knows
// what to fix first.
// ─────────────────────────────────────────────────────────────────────────
function StripeLiveSetupPanel({ meta }) {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);          // {ok, catalog, drift_warnings, ...}
  const [error, setError] = useState(null);
  const [showWebhook, setShowWebhook] = useState(false);
  const [whsecInput, setWhsecInput] = useState("");
  const [whsecChecking, setWhsecChecking] = useState(false);
  const [whsecResult, setWhsecResult] = useState(null);
  // Phase C2.1-followup — track WHICH input string was just verified so the
  // "Save to vault" button can only fire on a secret the operator has
  // successfully round-tripped against the Stripe SDK signature checker.
  // Cleared the moment they edit the input again.
  const [verifiedSecret, setVerifiedSecret] = useState(null);
  const [savedMeta, setSavedMeta] = useState(null);    // {fingerprint, last_rotated_at, mode}
  const [savingSecret, setSavingSecret] = useState(false);

  // Pre-load existing webhook-secret metadata so the panel can render the
  // "saved fingerprint · last rotated …" line without needing the plaintext.
  useEffect(() => {
    let alive = true;
    api.get("/super-admin/integrations/stripe/webhook-secret-meta")
      .then(({ data }) => { if (alive && data?.set) setSavedMeta(data); })
      .catch(() => { /* not set yet — leave savedMeta null */ });
    return () => { alive = false; };
  }, []);

  const ready = meta.mode === "live" && !!meta.fingerprint;

  // Built once so we can copy without inline-stringifying every render.
  const webhookUrl = "https://entrybook.site/api/stripe/webhook";
  const webhookEvents = [
    "checkout.session.completed",
    "customer.subscription.created",
    "customer.subscription.updated",
    "customer.subscription.deleted",
    "invoice.paid",
    "invoice.payment_failed",
    "customer.updated",
  ];

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed — select the text manually");
    }
  };

  const runSetup = async () => {
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const { data } = await api.post("/super-admin/stripe/run-setup");
      setResult(data);
      const created = (data.catalog || []).filter((r) => r.created_or_existing === "created").length;
      const existing = (data.catalog || []).filter((r) => r.created_or_existing === "existing").length;
      toast.success(
        `Stripe catalogue synced — ${created} created, ${existing} existing` +
        (data.drift_warnings?.length ? ` · ${data.drift_warnings.length} drift warning(s)` : ""),
      );
    } catch (e) {
      const det = e?.response?.data?.detail;
      const friendly = det?.message || formatApiError(e);
      setError(friendly);
      toast.error(friendly);
    } finally {
      setRunning(false);
    }
  };

  const testWebhookSecret = async () => {
    setWhsecChecking(true);
    setWhsecResult(null);
    try {
      const { data } = await api.post("/super-admin/stripe/webhook-test",
                                       { whsec: whsecInput.trim() });
      setWhsecResult(data);
      if (data.ok) {
        // Lock-in the exact string we just verified so the Save button can
        // gate on input === verifiedSecret. Any subsequent keystroke that
        // diverges hides the Save button again.
        setVerifiedSecret(whsecInput.trim());
        toast.success("Webhook secret verified");
      } else {
        setVerifiedSecret(null);
        toast.error(data.error || "Verification failed");
      }
    } catch (e) {
      setVerifiedSecret(null);
      toast.error(formatApiError(e));
    } finally {
      setWhsecChecking(false);
    }
  };

  const saveWebhookSecret = async () => {
    setSavingSecret(true);
    try {
      const { data } = await api.post(
        "/super-admin/integrations/stripe/save-webhook-secret",
        { webhook_secret: whsecInput.trim() },
      );
      setSavedMeta({
        fingerprint: data.fingerprint,
        last_rotated_at: data.last_rotated_at,
        mode: data.mode,
      });
      // Security — wipe plaintext from React state the moment Mongo has it.
      setWhsecInput("");
      setVerifiedSecret(null);
      setWhsecResult(null);
      toast.success("Webhook secret saved to vault");
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(det?.message || formatApiError(e));
    } finally {
      setSavingSecret(false);
    }
  };

  return (
    <div className="mt-4 bg-white/70 border border-indigo-200 rounded-xl p-4"
         data-testid="stripe-live-setup-panel">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h4 className="text-sm font-bold text-navy-900 inline-flex items-center gap-1.5">
          <Zap className="w-4 h-4 text-orange-500" /> Live setup
        </h4>
        {ready
          ? <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-success-50 text-success-700 border border-success-200">Ready</span>
          : <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-warning-50 text-warning-700 border border-warning-200">Awaiting credentials</span>}
      </div>

      {!ready && (
        <p className="text-xs text-warning-700 bg-warning-50 border border-warning-200 rounded-md px-3 py-2"
           data-testid="stripe-setup-awaiting">
          Add your live Stripe key above and set the mode to live before running setup.
        </p>
      )}

      {ready && (
        <>
          <Button
            onClick={runSetup}
            disabled={running}
            className="bg-orange-500 hover:bg-orange-600 text-white w-full justify-center"
            data-testid="stripe-run-setup-btn"
          >
            {running
              ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Talking to Stripe…</>
              : <><Zap className="w-4 h-4 mr-1.5" /> Run Stripe setup</>}
          </Button>
          <p className="text-[11px] text-navy-500 mt-2 leading-snug">
            Creates 4 products (Solo £12, Standard £29, Pro £69, Site add-on £15) in your Stripe
            account and stores the Price IDs in EntryBook. Safe to re-run — won't duplicate.
          </p>
        </>
      )}

      {error && (
        <div className="mt-3 rounded-lg border bg-danger-50 border-danger-200 text-danger-700 px-3 py-2 text-xs"
             data-testid="stripe-setup-error">
          <span className="inline-flex items-center gap-1.5 font-semibold">
            <XCircle className="w-3.5 h-3.5" /> {error}
          </span>
        </div>
      )}

      {result && (
        <div className="mt-4" data-testid="stripe-setup-result">
          {result.drift_warnings?.length > 0 && (
            <div className="mb-3 rounded-lg border bg-warning-50 border-warning-300 text-warning-800 px-3 py-2 text-xs"
                 data-testid="stripe-setup-drift">
              <div className="inline-flex items-center gap-1.5 font-semibold mb-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Drift detected — Stripe price disagrees with EntryBook
              </div>
              <ul className="ml-5 list-disc">
                {result.drift_warnings.map((d) => (
                  <li key={d.lookup_key}>
                    <code className="font-mono">{d.plan_id}</code>: Stripe shows{" "}
                    <strong>{d.stripe_amount_minor / 100}</strong> · catalogue expects{" "}
                    <strong>{d.catalogue_amount_minor / 100}</strong>. Reconcile in the Stripe dashboard.
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="overflow-hidden rounded-lg border border-navy-100">
            <table className="w-full text-xs">
              <thead className="bg-navy-50 text-navy-600 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Plan</th>
                  <th className="text-left px-3 py-2">Price ID</th>
                  <th className="text-left px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2">State</th>
                </tr>
              </thead>
              <tbody>
                {(result.catalog || []).map((r) => (
                  <tr key={r.plan_id} className="border-t border-navy-100"
                      data-testid={`stripe-catalog-row-${r.plan_id}`}>
                    <td className="px-3 py-2 font-semibold">{r.plan_id}</td>
                    <td className="px-3 py-2 font-mono select-all">{r.price_id || r.error}</td>
                    <td className="px-3 py-2">£{(r.amount_minor || 0) / 100}</td>
                    <td className="px-3 py-2">
                      {r.created_or_existing === "created"
                        ? <span className="inline-flex items-center gap-1 text-success-700"><CheckCircle2 className="w-3 h-3" /> created</span>
                        : r.created_or_existing === "existing"
                          ? <span className="inline-flex items-center gap-1 text-navy-500"><CheckCircle2 className="w-3 h-3" /> existing</span>
                          : <span className="inline-flex items-center gap-1 text-danger-700"><XCircle className="w-3 h-3" /> error</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] text-navy-400">
            Price IDs are stored in EntryBook's `stripe_catalog` collection. Re-runs are idempotent.
          </p>
        </div>
      )}

      {/* Next step — collapsed webhook setup. Even if setup hasn't run
          yet, the operator can use this panel ahead of time. */}
      <div className="mt-4 border-t border-navy-100 pt-3">
        <button
          type="button"
          onClick={() => setShowWebhook((v) => !v)}
          className="w-full text-left flex items-center justify-between gap-2 text-sm font-semibold text-navy-800 hover:text-navy-900"
          data-testid="stripe-webhook-toggle"
        >
          <span className="inline-flex items-center gap-1.5">
            <Webhook className="w-4 h-4 text-indigo-500" /> Next step — register the webhook
          </span>
          {showWebhook
            ? <ChevronUp className="w-4 h-4 text-navy-400" />
            : <ChevronDown className="w-4 h-4 text-navy-400" />}
        </button>
        {showWebhook && (
          <div className="mt-3 space-y-3" data-testid="stripe-webhook-panel">
            <div>
              <div className="eb-overline mb-1">Endpoint URL</div>
              <div className="flex items-stretch gap-2">
                <code className="flex-1 select-all font-mono text-xs bg-navy-50 border border-navy-100 rounded-md px-2.5 py-2"
                      data-testid="stripe-webhook-url">{webhookUrl}</code>
                <Button size="sm" variant="outline" onClick={() => copyText(webhookUrl, "Webhook URL")}
                        data-testid="stripe-webhook-copy-url">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
              <p className="text-[11px] text-navy-500 mt-1">
                In Stripe dashboard → Developers → Webhooks → Add endpoint → paste this URL.
              </p>
            </div>
            <div>
              <div className="eb-overline mb-1">Events to subscribe</div>
              <div className="flex items-stretch gap-2">
                <code className="flex-1 select-all font-mono text-[11px] bg-navy-50 border border-navy-100 rounded-md px-2.5 py-2 whitespace-pre-wrap"
                      data-testid="stripe-webhook-events">{webhookEvents.join("\n")}</code>
                <Button size="sm" variant="outline" onClick={() => copyText(webhookEvents.join("\n"), "Events list")}
                        data-testid="stripe-webhook-copy-events">
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            <div>
              <div className="eb-overline mb-1">Save the signing secret</div>
              <p className="text-[11px] text-navy-500 mb-1">
                Stripe will show a <code className="font-mono">whsec_…</code> value after you create
                the endpoint. Paste it into the Integration Vault →{" "}
                <code className="font-mono">stripe_webhook_secret</code>.
              </p>
              <div className="flex items-stretch gap-2">
                <input
                  type="password"
                  value={whsecInput}
                  onChange={(e) => {
                    setWhsecInput(e.target.value);
                    // Edits invalidate the verified state — the operator must
                    // re-test before they can hit Save. Prevents save-after-
                    // verify-different-value bugs.
                    if (verifiedSecret !== null) setVerifiedSecret(null);
                    if (whsecResult !== null) setWhsecResult(null);
                  }}
                  placeholder="whsec_..."
                  className="eb-input flex-1 text-xs"
                  data-testid="stripe-webhook-secret-input"
                />
                <Button size="sm" onClick={testWebhookSecret}
                        disabled={whsecChecking || !whsecInput.trim().startsWith("whsec_")}
                        className="bg-navy-700 hover:bg-navy-800 text-white"
                        data-testid="stripe-webhook-test-btn">
                  {whsecChecking ? "Checking…" : "Test webhook signature"}
                </Button>
              </div>
              {whsecResult && (
                <div className={`mt-2 rounded-md border px-3 py-2 text-xs ${
                  whsecResult.ok
                    ? "bg-success-50 border-success-200 text-success-700"
                    : "bg-danger-50 border-danger-200 text-danger-700"
                }`} data-testid="stripe-webhook-test-result">
                  {whsecResult.ok
                    ? <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> {whsecResult.message}</span>
                    : <span className="inline-flex items-center gap-1.5"><XCircle className="w-3.5 h-3.5" /> {whsecResult.error}</span>}
                </div>
              )}

              {/* Phase C2.1-followup — "Save to vault" appears only when the
                  in-input string is the SAME string we just verified. Any
                  divergence (re-edit) hides this button until re-tested. */}
              {verifiedSecret && verifiedSecret === whsecInput.trim() && (
                <div className="mt-2 flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={saveWebhookSecret}
                    disabled={savingSecret}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="stripe-webhook-save-btn"
                  >
                    {savingSecret
                      ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Saving…</>
                      : <><Save className="w-3.5 h-3.5 mr-1" /> Save to vault</>}
                  </Button>
                  <span className="text-[11px] text-navy-500">
                    Encrypted with AES-256-GCM. Plaintext is never stored on disk or returned.
                  </span>
                </div>
              )}

              {savedMeta && (
                <div className="mt-3 rounded-md border bg-success-50 border-success-200 text-success-700 px-3 py-2 text-xs"
                     data-testid="stripe-webhook-saved-chip">
                  <div className="inline-flex items-center gap-1.5 font-semibold">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Webhook secret saved ✓
                  </div>
                  <p className="mt-1 text-[11px] opacity-90">
                    Fingerprint <code className="font-mono">{savedMeta.fingerprint}</code>
                    {savedMeta.last_rotated_at && (
                      <> · last rotated <code className="font-mono">{savedMeta.last_rotated_at.replace("T", " ").slice(0, 19)}Z</code></>
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ----- v2 PR-2A — Stripe one-click test result components -----

const STRIPE_STATUS_STYLES = {
  connected: {
    bg: "bg-success-50 border-success-200 text-success-700",
    Icon: CheckCircle2,
    label: "Connected",
  },
  invalid: {
    bg: "bg-danger-50 border-danger-200 text-danger-700",
    Icon: XCircle,
    label: "Invalid key",
  },
  expired: {
    bg: "bg-danger-50 border-danger-200 text-danger-700",
    Icon: Clock,
    label: "Expired",
  },
  restricted: {
    bg: "bg-warning-50 border-warning-300 text-warning-700",
    Icon: ShieldAlert,
    label: "Restricted",
  },
  error: {
    bg: "bg-danger-50 border-danger-200 text-danger-700",
    Icon: AlertTriangle,
    label: "Error",
  },
};

function StripeProbeResult({ probe, integration = "stripe" }) {
  const style = STRIPE_STATUS_STYLES[probe.status] || STRIPE_STATUS_STYLES.error;
  const Icon = style.Icon;
  const errSummary = probe.stripe_error_summary || probe.google_error_summary;
  return (
    <div className={`col-span-2 rounded-lg p-3 border text-xs ${style.bg}`}
         data-testid={`test-result-${integration}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="inline-flex items-center gap-1.5 font-semibold">
          <Icon className="w-3.5 h-3.5" />
          <span data-testid={`test-${integration}-status-pill`}>{style.label}</span>
          <span className="font-normal text-[10px] opacity-70">· {probe.latency_ms} ms · path {probe.active_path}</span>
        </div>
      </div>
      {probe.message && <p className="mt-1 leading-snug">{probe.message}</p>}
      {probe.status === "connected" && integration === "stripe" && (probe.available_count !== null || probe.pending_count !== null) && (
        <p className="mt-1 text-[11px] opacity-80">
          Balance: {probe.available_count ?? 0} available · {probe.pending_count ?? 0} pending currencies.
        </p>
      )}
      {errSummary && (
        <p className="mt-1 text-[11px] font-mono break-all opacity-80">{errSummary}</p>
      )}
    </div>
  );
}

function StripeCustomerResult({ result }) {
  if (result.ok) {
    return (
      <div className="col-span-2 rounded-lg p-3 border text-xs bg-success-50 border-success-200 text-success-700"
           data-testid="test-customer-result-stripe">
        <div className="inline-flex items-center gap-1.5 font-semibold">
          <UserRoundCheck className="w-3.5 h-3.5" />
          Write test passed · {result.duration_ms} ms
        </div>
        <p className="mt-1 leading-snug">
          Customer <code className="font-mono">{result.created?.customer_id}</code> created and deleted cleanly. Live Stripe write access verified.
        </p>
      </div>
    );
  }
  if (result.partial === "created_not_deleted") {
    return (
      <div className="col-span-2 rounded-lg p-3 border text-xs bg-warning-50 border-warning-300 text-warning-800"
           data-testid="test-customer-result-stripe">
        <div className="inline-flex items-center gap-1.5 font-semibold">
          <AlertTriangle className="w-3.5 h-3.5" />
          Orphan customer — manual cleanup required
        </div>
        <p className="mt-1 leading-snug">
          The test customer was created in Stripe but the auto-cleanup delete failed. Delete <code className="font-mono">{result.customer_id}</code> manually in your Stripe dashboard.
        </p>
        {result.error_summary && (
          <p className="mt-1 text-[11px] font-mono break-all opacity-80">{result.error_summary}</p>
        )}
      </div>
    );
  }
  return (
    <div className="col-span-2 rounded-lg p-3 border text-xs bg-danger-50 border-danger-200 text-danger-700"
         data-testid="test-customer-result-stripe">
      <div className="inline-flex items-center gap-1.5 font-semibold">
        <XCircle className="w-3.5 h-3.5" />
        Write test failed
      </div>
      <p className="mt-1 leading-snug">No customer was created. {result.error_summary}</p>
    </div>
  );
}


// ----- Phase C1.2 — rich renderer for failed Twilio test-connection. ---------
// Parses the numeric Twilio code out of the `error` string and surfaces:
//   • a copyable chip showing the code + the human Twilio message
//   • a clickable `more_info` docs link
//   • a one-line plain-English hint for the top 3 auth-failure codes
// The user should never have to open DevTools to understand a 401 again.
const TWILIO_HINTS = {
  20003: "Account SID or API Key SID is wrong. Re-check both last-4 values on the card.",
  20008: "Twilio has suspended this account — log into Twilio console to resolve.",
  20404: "The Account SID does not exist in Twilio. Check for typos.",
};


// ----- Phase C1.2.1 — Saved-credentials panel with per-field last-4 reveal ---
function TwilioHintsPanel({ hints }) {
  const [reveal, setReveal] = useState(false);
  // Each row: [label, prefix shown, last4 value, fallback when no value].
  const fields = [
    { label: "Account SID",    prefix: "AC", last4: hints.account_sid_last4 },
    { label: "API Key SID",    prefix: "SK", last4: hints.api_key_sid_last4 },
    { label: "API Key Secret", prefix: "",   last4: hints.api_key_secret_last4 },
    { label: "Auth Token",     prefix: "",   last4: hints.auth_token_last4 },
  ];
  return (
    <div className="col-span-2 bg-white/60 rounded-lg p-3 border border-white text-navy-700 space-y-1"
         data-testid="twilio-hints">
      <div className="flex items-center justify-between gap-2">
        <span className="text-navy-500">Auth mode</span>
        <span className="font-semibold uppercase tracking-wider text-[10px]">
          {hints.auth_mode === "api_key"
            ? <span className="text-emerald-700">API KEY (Standard)</span>
            : <span className="text-amber-700">Legacy Auth Token</span>}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-navy-500">From number</span>
        <span className="font-mono">{hints.from_number || "—"}</span>
      </div>

      <div className="pt-2 border-t border-navy-100 flex items-center justify-between">
        <span className="text-navy-500 text-[11px]">Saved credentials (last 4)</span>
        <button
          type="button"
          onClick={() => setReveal((v) => !v)}
          className="text-[10px] uppercase tracking-wider text-orange-600 hover:text-orange-700 font-medium"
          data-testid="twilio-show-last4-toggle"
        >
          {reveal ? "Hide last 4" : "Show last 4"}
        </button>
      </div>

      {fields.map((f) => f.last4 && (
        <div key={f.label} className="flex items-center justify-between gap-2"
             data-testid={`twilio-last4-${f.label.toLowerCase().replace(/\s+/g, "-")}`}>
          <span className="text-navy-500">{f.label}</span>
          <span className="font-mono">
            {reveal
              ? <>{f.prefix}…<strong className="text-navy-900">{f.last4}</strong></>
              : <span className="text-navy-300">{f.prefix}…••••</span>}
          </span>
        </div>
      ))}

      {/* If no credential-field last-4 is present, the operator likely
          saved BEFORE the hints feature shipped — surface a one-line
          nudge so they re-save to populate the panel. */}
      {!fields.some((f) => f.last4) && (
        <div className="mt-2 px-2.5 py-1.5 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-[11px]">
          Re-save credentials to populate the last-4 panel (added after your initial save).
        </div>
      )}
    </div>
  );
}


function TwilioFailureRenderer({ result }) {
  const errText = String(result?.error || "");
  const codeMatch = errText.match(/"code"\s*:\s*(\d+)/);
  const msgMatch = errText.match(/"message"\s*:\s*"([^"]+)"/);
  const moreInfoMatch = errText.match(/"more_info"\s*:\s*"([^"]+)"/);
  const code = codeMatch ? Number(codeMatch[1]) : null;
  const humanMessage = msgMatch ? msgMatch[1] : null;
  const moreInfoUrl = moreInfoMatch ? moreInfoMatch[1] : null;
  const hint = code != null ? TWILIO_HINTS[code] : null;
  const httpStatus = result?.error_class || "http_error";

  const copyChip = async () => {
    const text = code != null && humanMessage
      ? `Twilio error ${code} — ${humanMessage}`
      : errText;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch { toast.error("Could not copy"); }
  };

  return (
    <div className="col-span-2 rounded-lg p-3 border bg-danger-50 border-danger-200 text-danger-700 text-xs"
         data-testid="test-result-twilio">
      <div className="inline-flex items-center gap-1.5 font-medium">
        <XCircle className="w-3 h-3" /> Twilio rejected the credentials ({httpStatus}).
      </div>

      {code != null ? (
        <button
          type="button"
          onClick={copyChip}
          className="mt-2 inline-flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-white border border-danger-300 hover:bg-danger-100 transition-colors text-danger-900"
          data-testid="twilio-error-chip"
          title="Click to copy"
        >
          <span className="font-mono font-semibold">Twilio error {code}</span>
          {humanMessage && <span className="text-navy-700 font-normal">— {humanMessage}</span>}
          <Copy className="w-3 h-3 text-navy-400" />
        </button>
      ) : (
        // Couldn't parse the code — fall back to raw text so the operator
        // still sees something actionable.
        <div className="mt-2 font-mono text-[11px] break-words text-danger-900">{errText}</div>
      )}

      {hint && (
        <div className="mt-2 px-2.5 py-1.5 rounded-md bg-amber-50 border border-amber-200 text-amber-900"
             data-testid="twilio-error-hint">
          {hint}
        </div>
      )}

      {moreInfoUrl && (
        <a
          href={moreInfoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-orange-600 hover:text-orange-700 hover:underline"
          data-testid="twilio-error-docs"
        >
          Twilio docs <ExternalLink className="w-3 h-3" />
        </a>
      )}
    </div>
  );
}



// ----- v1.1.2 — copy-on-click build fingerprint for production debugging -----
function BuildFingerprint() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/__version")
      .then(({ data }) => { if (alive) setInfo(data); })
      .catch(() => { /* silent — non-critical */ });
    return () => { alive = false; };
  }, []);

  if (!info) return null;

  const summary = `Build: ${info.git_sha} · ${info.build_timestamp}`;
  const full = JSON.stringify(info, null, 2);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(full);
      toast.success("Build fingerprint copied to clipboard");
    } catch {
      toast.error("Copy failed — select the text manually");
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      title={full}
      className="mt-3 block text-xs font-mono text-navy-400 hover:text-navy-600 select-all cursor-pointer"
      data-testid="build-fingerprint"
    >
      {summary}
    </button>
  );
}
